import { useMemo } from 'react'
import { Flow } from '../types'

export interface RenewableLicense {
  code: string
  licenseType: string
  flow: Flow
  endsAt?: number
}

const ONE_YEAR = 365 * 24 * 60 * 60 * 1000

const FLOW_OF: Record<string, Flow> = {
  school: 'school',
  schoolpro: 'school',
  classroom: 'teacher',
  classroompro: 'teacher',
  homeschool: 'family',
  homeschoolpro: 'family',
}

export function renewedEndsAt(license: RenewableLicense, now: number): number {
  return Math.max(license.endsAt ?? 0, now) + ONE_YEAR
}

// Only these can be renewed in place; the server refuses the rest.
const RENEWABLE_TYPES = ['school', 'classroom', 'homeschool']

/** The license of this exact type that the account administers, if it has one to extend. */
export function renewableFor(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentTeacher: any,
  licenseType: string
): RenewableLicense | null {
  if (!RENEWABLE_TYPES.includes(licenseType)) return null
  const admin = currentTeacher?.adminLicenseInfos || {}
  const code = Object.keys(admin).find(
    (key) => admin[key]?.licenseType === licenseType
  )
  return code ? renewableLicense(currentTeacher, code) : null
}

function lookup(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentTeacher: any,
  lCode: string | null,
  adminOnly: boolean
): RenewableLicense | null {
  if (!lCode) return null
  const admin = currentTeacher?.adminLicenseInfos || {}
  const info = adminOnly
    ? admin[lCode]
    : admin[lCode] ?? (currentTeacher?.licenseInfos || {})[lCode]
  const flow = FLOW_OF[info?.licenseType]
  if (!flow) return null
  return {
    code: info.code ?? lCode,
    licenseType: info.licenseType,
    flow,
    endsAt: info.endsAt,
  }
}

/** The license a renew link named, whether or not this account administers it. */
export function licenseByCode(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentTeacher: any,
  lCode: string | null
): RenewableLicense | null {
  return lookup(currentTeacher, lCode, false)
}

/** The same, but only when the account administers it — the server renews no other. */
export function renewableLicense(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentTeacher: any,
  lCode: string | null
): RenewableLicense | null {
  return lookup(currentTeacher, lCode, true)
}

export default function useRenewableLicense(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentTeacher: any,
  lCode: string | null
): RenewableLicense | null {
  return useMemo(
    () => licenseByCode(currentTeacher, lCode),
    [currentTeacher, lCode]
  )
}
